import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { getGuideBySlug, getRelatedGuides, GUIDES } from '../../guides/metadata';

function GuideDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const guide = getGuideBySlug(slug);
  const relatedGuides = guide ? getRelatedGuides(guide.id) : [];

  useEffect(() => {
    // Scroll to top when guide changes
    window.scrollTo(0, 0);

    if (!guide) {
      setError('Guide not found');
      setLoading(false);
      return;
    }

    // Load the markdown file
    setLoading(true);
    import(`../../guides/${guide.id}.md`)
      .then(module => {
        return fetch(module.default);
      })
      .then(response => response.text())
      .then(text => {
        setMarkdownContent(text);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading guide:', err);
        setError('Failed to load guide content');
        setLoading(false);
      });
  }, [guide]);

  if (loading) {
    return (
      <div className="guide-detail-container">
        <div className="guide-loading">Loading guide...</div>
      </div>
    );
  }

  if (error || !guide) {
    return (
      <div className="guide-detail-container">
        <div className="guide-error">
          <h1>Guide Not Found</h1>
          <p>{error || 'The requested guide could not be found.'}</p>
          <Link to="/guides" className="button-primary">Back to Guides</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="guide-detail-container">
      <Helmet>
        <title>{guide.title} - Ref-Lex Guides</title>
        <meta name="description" content={guide.description} />
        <meta name="keywords" content={guide.keywords.join(', ')} />
      </Helmet>

      {/* Breadcrumb Navigation */}
      <nav className="guide-breadcrumb">
        <a href="/">Home</a>
        <span className="breadcrumb-separator">›</span>
        <Link to="/guides">Guides</Link>
        <span className="breadcrumb-separator">›</span>
        <span className="breadcrumb-current">{guide.title}</span>
      </nav>

      <div className="guide-detail-content">
        {/* Guide Header */}
        <div className="guide-header">
          <div className="guide-header-text">
            <h1 className="guide-detail-title">{guide.title}</h1>
            <div className="guide-meta-bar">
              <span className="guide-category-badge">{guide.category}</span>
              <span className="guide-difficulty-badge">{guide.difficulty}</span>
              <span className="guide-time-badge">{guide.estimatedTime}</span>
            </div>
            <p className="guide-detail-description">{guide.description}</p>
          </div>
        </div>

        {/* Markdown Content */}
        <div className="guide-markdown-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={oneDark}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
              a({ node, children, href, ...props }) {
                // Handle internal guide links
                if (href && href.startsWith('/guides/')) {
                  return (
                    <Link to={href} {...props}>
                      {children}
                    </Link>
                  );
                }
                // External links open in new tab
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                    {children}
                  </a>
                );
              },
            }}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>

        {/* Related Guides */}
        {relatedGuides.length > 0 && (
          <div className="guide-related-section">
            <h2>Related Guides</h2>
            <div className="guide-related-grid">
              {relatedGuides.map(relatedGuide => (
                <Link
                  key={relatedGuide.id}
                  to={`/guides/${relatedGuide.slug}`}
                  className="guide-related-card"
                >
                  <h3>{relatedGuide.title}</h3>
                  <p>{relatedGuide.description}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Navigation Footer */}
        <div className="guide-navigation-footer">
          <button onClick={() => navigate('/guides')} className="button-secondary">
            ← Back to All Guides
          </button>
          <Link to="/login" className="button-primary">
            Get Started with Ref-Lex →
          </Link>
        </div>
      </div>
    </div>
  );
}

export default GuideDetail;
